import { Fragment } from "react";

/**
 * Manifesto.jsx
 * -----------------------------------------------------------------------
 * The GILDer manifesto ("Why did we build GILDer?") plus the GILDFoundation
 * announcement. Styled to match the public FAQ / Protocol Health chrome —
 * a cream page backdrop and dark-green panels with a gold ring — but given
 * an editorial, statement-piece treatment: a warm ivory body, bright-gold
 * emphasis on the manifesto's recurring "We built it…" / "We don't want…"
 * refrains, a soft gold glow for depth, and a highlighted closing line.
 *
 * Copy is data-driven. Each entry is a "stanza" (an array of lines)
 * rendered as one paragraph with soft breaks between lines. Inline
 * `**bold**` emphasis, `[label](url|blank)` markdown links and the `<here>`
 * placeholder are handled by RichLine so mixed content stays styled.
 * -----------------------------------------------------------------------
 */

// Stanzas of the manifesto. `**…**` marks the anaphoric refrain so the
// repeated openings glow gold and give the wall of text a rhythm. The
// closing statement is pulled out (MANIFESTO_CLOSING) for its own treatment.
const MANIFESTO = [
    [
        "**We built it** because it was the product we wanted to use ourselves - and no one else would build it",
        "**We built it** because we work hard for our money and then we want our money to work hard for us.",
        "**We built it** because compound interest IS the 8th wonder of the world, but most of us don’t have the patience to watch our money grow at a few percentage points a year."
    ],
    [
        "**We don’t want** to be constantly checking our investments every day.",
        "**We don’t want** to have to move them from one financial product, or protocol, to another constantly chasing the best rates.",
        "**We don’t want** to be making bets on companies, currencies or markets when insiders and others have way more information than us."
    ],
    ["Trading volatility of the markets might be fun in the short term, but we no longer have the constitution to watch our life savings peak and trough."],
    ["**We built GILDer** because some people already get access to reliable, double digit returns, but those returns are gatekept by Private Banks who only serve the wealthiest in society."],
    ["Even then, wealthy or aspiring, you are never presented with the best investment opportunity. You are presented with the opportunity that pays the best commissions."],
    ["**We built GILDer** because elegant, transparent, maths on-chain, beats gambling or being at the mercy of human emotions."],
    ["**We built it** because the laws of supply and demand state that when there is demand for something with a finite supply the price goes up.  And we can return those gains to you as yield."],
    ["**We built it** because it was the product we wanted to use ourselves."]
];

const MANIFESTO_CLOSING = "We built it so we can all have a financial future to depend on.";

const FOUNDATION = [
    [
        "When the GILDer Protocol is in ‘profit’ it sends 1/6th of that profit to an Operations wallet to pay for the ongoing upkeep of the protocol.",
        "1% of that money goes directly to the GILDFoundation."
    ],
    ["The GILDFoundation exists to help those whose priority is not getting the best yield on their assets, but just on having enough food & water for their family today.  A chance to put their children through education and to help them have a better life."],
    ["The GILDFoundation is fully funded by the GILDer Protocol.  First to pay the fees and then to contribute to charities that are helping those with the most pressing needs.  100% of all funds are audited and given directly to those who need them.  A full report of the giving, the charities and the audits are available at [GILDFoundation](http://GILDFoundation.com|blank) site (coming soon)"],
    ["If you have got value from using the GILDer Protocol and would also like to make a contribution to these causes you can do so <here>, our commitment to you is that 100% of your funds will go to the cause you select and we thank you for the support.  We pay the fees so your money goes only where it matters."]
];

const QUOTES = {
    bunyan: {
        text: "You have not lived today until you have done something for someone who can never repay you.",
        author: "John Bunyan"
    },
    holmes: {
        text: "There is no exercise better for the heart than reaching down and lifting people up.",
        author: "John Holmes"
    }
};

// Inline tokens: `[label](url|blank)` markdown links and the `<here>` placeholder.
const INLINE_RE = /(\[[^\]]+\]\([^)]+\)|<here>)/g;

/** Renders link / <here> tokens inside a plain (non-bold) text segment. */
function renderTokens(text, keyPrefix) {
    return text.split(INLINE_RE).map((part, i) => {
        if (!part) return null;
        const key = `${keyPrefix}-${i}`;

        const md = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (md) {
            const [, label, href] = md;
            const [url, target] = href.split("|");
            const isBlank = target === "blank";
            return (
                <a
                    key={key}
                    href={url}
                    target={isBlank ? "_blank" : "_self"}
                    rel={isBlank ? "noopener noreferrer" : undefined}
                    className="font-semibold text-[#FBC206] underline decoration-[#FBC206]/50 underline-offset-2 hover:decoration-[#FBC206]"
                >
                    {label}
                </a>
            );
        }

        if (part === "<here>") {
            return (
                <span
                    key={key}
                    title="Coming soon"
                    className="cursor-help font-semibold text-[#FBC206]/80 underline decoration-dotted decoration-[#FBC206]/40 underline-offset-2"
                >
                    here
                </span>
            );
        }

        return <Fragment key={key}>{part}</Fragment>;
    });
}

/** Renders one line, turning `**…**` into bright-gold emphasis. */
function RichLine({ text }) {
    return text.split("**").map((seg, i) =>
        i % 2 === 1 ? (
            <strong key={i} className="font-semibold text-[#FBC206]">
                {renderTokens(seg, `b${i}`)}
            </strong>
        ) : (
            <Fragment key={i}>{renderTokens(seg, `n${i}`)}</Fragment>
        )
    );
}

/** A stanza (array of lines) rendered as one paragraph with soft breaks. */
function Stanza({ lines }) {
    return (
        <p className="text-pretty text-[16px] leading-[1.8] text-[#ece7d6]/90">
            {lines.map((line, i) => (
                <Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    <RichLine text={line} />
                </Fragment>
            ))}
        </p>
    );
}

/** A dark-green content panel with a soft gold glow, matching the app chrome. */
function Panel({ children }) {
    return (
        <section className="relative overflow-hidden rounded-[16px] bg-[#10281b] ring-1 ring-[#bfb67b] p-7 shadow-[0_24px_60px_-40px_rgba(10,31,16,0.9)] sm:p-10">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(251,194,6,0.10),transparent)]"
            />
            <div className="relative mx-auto max-w-[62ch] space-y-6">{children}</div>
        </section>
    );
}

/** Centered gold label flanked by hairline rules. */
function SectionDivider({ text }) {
    return (
        <div className="my-2 flex items-center gap-4">
            <span className="h-px flex-1 bg-[#bfb67b]/45" />
            <span className="font-manrope text-[11px] font-bold uppercase tracking-[0.28em] text-[#c99a1e]">
                {text}
            </span>
            <span className="h-px flex-1 bg-[#bfb67b]/45" />
        </div>
    );
}

/** A small decorative divider used where a text label would be redundant. */
function DotDivider() {
    return (
        <div className="my-2 flex items-center justify-center gap-2" aria-hidden="true">
            <span className="h-1 w-1 rounded-full bg-[#c99a1e]/45" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#c99a1e]/70" />
            <span className="h-1 w-1 rounded-full bg-[#c99a1e]/45" />
        </div>
    );
}

/** A white pull-quote card with a gold top accent and decorative quote mark. */
function Quote({ text, author }) {
    return (
        <figure className="relative overflow-hidden rounded-[16px] border border-[#bfb67b]/50 bg-white px-8 py-10 text-center shadow-[0_18px_40px_-28px_rgba(14,42,26,0.35)]">
            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-[#FBC206]" />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute left-6 top-3 select-none font-serif text-[72px] leading-none text-[#FBC206]/25"
            >
                &ldquo;
            </span>
            <blockquote className="relative mx-auto max-w-[52ch] text-balance font-serif text-[19px] italic leading-relaxed text-[#15351c] sm:text-[21px]">
                {text}
            </blockquote>
            <figcaption className="mt-5 font-manrope text-[13px] font-semibold uppercase tracking-[0.15em] text-[#5e7a5f]">
                — {author}
            </figcaption>
        </figure>
    );
}

/**
 * Manifesto page. Reads as an editorial statement: a hero header, the
 * manifesto body with a glowing refrain and a highlighted closing line,
 * then the GILDFoundation announcement bookended by two pull-quotes.
 */
export default function Manifesto() {
    return (
        <div className="min-h-screen w-full bg-[#f6f6ec]">
            <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-7 px-4 py-10 sm:px-6 lg:px-8">
                {/* Header */}
                <header className="relative overflow-hidden rounded-[16px] bg-[#10281b] ring-1 ring-[#bfb67b] px-6 py-10 text-center shadow-[0_24px_60px_-40px_rgba(10,31,16,0.9)]">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(251,194,6,0.12),transparent)]"
                    />
                    <div className="relative">
                        <p className="font-manrope text-[11px] font-bold uppercase tracking-[0.34em] text-[#FBC206]">
                            Our Manifesto
                        </p>
                        <h1 className="mt-3 text-balance font-serif text-[32px] font-semibold leading-tight text-white sm:text-[42px]">
                            Why did we build GILDer?
                        </h1>
                        <span className="mx-auto mt-5 block h-px w-16 bg-[#FBC206]/70" />
                    </div>
                </header>

                {/* Manifesto body */}
                <Panel>
                    {MANIFESTO.map((lines, i) => (
                        <Stanza key={i} lines={lines} />
                    ))}
                    <p className="mt-9 border-t border-[#bfb67b]/25 pt-7 text-center text-balance font-serif text-[21px] font-medium italic leading-snug text-[#FBC206] sm:text-[25px]">
                        {MANIFESTO_CLOSING}
                    </p>
                </Panel>

                {/* GILDFoundation */}
                <SectionDivider text="Announcing the GILDFoundation" />

                <Quote text={QUOTES.bunyan.text} author={QUOTES.bunyan.author} />

                <Panel>
                    {FOUNDATION.map((lines, i) => (
                        <Stanza key={i} lines={lines} />
                    ))}
                </Panel>

                <DotDivider />

                <Quote text={QUOTES.holmes.text} author={QUOTES.holmes.author} />
            </div>
        </div>
    );
}
